import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { NAV, SITE } from "@/lib/site";
import { ctaClick } from "@/lib/ctaTracking";

type NavItem = (typeof NAV)[number];

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) toggleRef.current?.focus();
  }, []);

  // Lock background scroll while the mobile panel is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Escape closes the mobile panel; Tab is kept inside it while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMenu();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      const toggle = toggleRef.current;
      if (!panel || !toggle) return;
      const focusables = [
        toggle,
        ...Array.from(
          panel.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        ),
      ].filter((el) => el.offsetParent !== null || el === toggle);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeMenu]);

  const desktopItems = NAV.filter((n) => n.to !== "/" && n.to !== "/contact");

  return (
    <header
      className={`fixed top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "border-b border-border bg-background/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <a
        href="#main"
        className={`sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:rounded-full focus:bg-primary focus:px-4 focus:py-2 focus:text-[13px] focus:font-medium focus:text-primary-foreground ${focusRing}`}
      >
        Skip to main content
      </a>
      <div className="container-page flex h-16 items-center justify-between gap-4">
        <Link
          to="/"
          className={`flex items-center gap-2 group rounded-md ${focusRing}`}
          aria-label={`${SITE.name} — home`}
        >
          <span className="text-base font-semibold tracking-tight">
            {SITE.name}
            <span className="text-primary">.</span>
          </span>
        </Link>

        <nav aria-label="Primary" className="hidden lg:flex h-16 items-stretch gap-3">
          {desktopItems.map((item) =>
            "children" in item && item.children ? (
              <DesktopDropdown key={item.to} item={item} />
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className={`inline-flex h-full items-center rounded-md px-2 text-[13px] font-medium text-ink-soft hover:text-primary transition-colors whitespace-nowrap ${focusRing}`}
                activeProps={{ className: "text-primary", "aria-current": "page" }}
              >
                {item.label}
              </Link>
            ),
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${SITE.phoneE164}`}
            onClick={ctaClick("phone", "header_desktop")}
            className={`inline-flex items-center gap-1.5 rounded-md px-1 py-1 text-[13px] font-medium text-ink-soft hover:text-primary transition-colors whitespace-nowrap ${focusRing}`}
            aria-label={`Call ${SITE.phone}`}
          >
            <Phone className="h-3.5 w-3.5" aria-hidden="true" />
            {SITE.phone}
          </a>
          <Link
            to="/contact"
            aria-label="Let's Talk — go to the contact page"
            className={`inline-flex items-center rounded-full bg-primary px-5 py-2 text-[13px] font-medium text-primary-foreground hover:opacity-90 transition-opacity whitespace-nowrap ${focusRing}`}
            activeProps={{ "aria-current": "page" }}
          >
            Let's Talk
          </Link>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <a
            href={`tel:${SITE.phoneE164}`}
            onClick={ctaClick("phone", "header_mobile")}
            aria-label={`Call ${SITE.phone}`}
            title={`Call ${SITE.phone}`}
            className={`inline-flex h-11 w-11 items-center justify-center rounded-full border border-border text-ink-soft hover:text-primary transition-colors ${focusRing}`}
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
          </a>
          <button
            ref={toggleRef}
            type="button"
            aria-label={open ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-haspopup="true"
            onClick={() => (open ? closeMenu(false) : setOpen(true))}
            className={`inline-flex h-11 w-11 items-center justify-center rounded-full border border-border ${focusRing}`}
          >
            {open ? <X className="h-4 w-4" aria-hidden="true" /> : <Menu className="h-4 w-4" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          ref={panelRef}
          id="mobile-nav"
          className="lg:hidden border-t border-border bg-background max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain"
        >
          <nav
            aria-label="Mobile"
            className="container-page py-4 grid gap-1 pb-[calc(1.5rem+env(safe-area-inset-bottom))]"
          >
            {NAV.map((item) => (
              <MobileNavItem key={item.to} item={item} onNavigate={() => closeMenu(false)} />
            ))}
            <Link
              to="/contact"
              onClick={() => closeMenu(false)}
              aria-label="Let's Talk — go to the contact page"
              className={`mt-2 inline-flex min-h-11 items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground ${focusRing}`}
              activeProps={{ "aria-current": "page" }}
            >
              Let's Talk
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

function DesktopDropdown({ item }: { item: NavItem & { children: ReadonlyArray<{ to: string; label: string }> } }) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLAnchorElement>(null);

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };
  // When the panel was opened by touch/keyboard, pointer-leave must not close
  // it — touch devices emit synthetic mouse events right after the tap.
  const stickyRef = useRef(false);
  const suppressFocusOpen = useRef(false);
  const scheduleClose = () => {
    cancelClose();
    if (stickyRef.current) return;
    closeTimer.current = setTimeout(() => setOpen(false), 260);
  };
  const close = () => {
    cancelClose();
    stickyRef.current = false;
    setOpen(false);
  };

  const focusItem = (index: number) => {
    const items = containerRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
    if (!items || items.length === 0) return;
    const i = (index + items.length) % items.length;
    items[i]?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) close();
    };
    const onFocusOut = (e: FocusEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.relatedTarget as Node)) close();
    };
    document.addEventListener("mousedown", onClickOutside);
    containerRef.current?.addEventListener("focusout", onFocusOut);
    const node = containerRef.current;
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      node?.removeEventListener("focusout", onFocusOut);
    };
  }, [open]);

  const currentItemIndex = () => {
    const items = Array.from(containerRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    return items.indexOf(document.activeElement as HTMLElement);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && open) {
      e.preventDefault();
      close();
      // Returning focus to the trigger must not immediately re-open the panel.
      suppressFocusOpen.current = true;
      triggerRef.current?.focus();
      window.setTimeout(() => (suppressFocusOpen.current = false), 300);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      stickyRef.current = true;
      const i = currentItemIndex();
      if (open && i >= 0) focusItem(i + 1);
      else {
        setOpen(true);
        requestAnimationFrame(() => focusItem(0));
      }
      return;
    }
    if (e.key === "ArrowUp" && open) {
      e.preventDefault();
      const i = currentItemIndex();
      requestAnimationFrame(() => focusItem(i >= 0 ? i - 1 : -1));
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative flex items-stretch"
      onKeyDown={onKeyDown}
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <Link
        ref={triggerRef}
        to={item.to}
        onFocus={() => {
          if (suppressFocusOpen.current) return;
          cancelClose();
          setOpen(true);
        }}
        className={`inline-flex h-full items-center rounded-md pl-2 pr-1 text-[13px] font-medium text-ink-soft hover:text-primary transition-colors whitespace-nowrap ${focusRing}`}
        activeProps={{ className: "text-primary", "aria-current": "page" }}
      >
        {item.label}
      </Link>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`${open ? "Close" : "Open"} ${item.label} submenu`}
        onClick={(e) => {
          cancelClose();
          // Mouse users already get hover-open; a click must never close the
          // panel out from under the pointer. Touch/pen/keyboard toggles.
          const pt = (e.nativeEvent as PointerEvent).pointerType;
          if (pt === "mouse") {
            setOpen(true);
            return;
          }
          if (open) close();
          else {
            stickyRef.current = true;
            setOpen(true);
          }
        }}
        className={`inline-flex h-full items-center rounded-md pr-2 pl-0.5 text-ink-soft hover:text-primary transition-colors ${focusRing}`}
      >
        <ChevronDown
          className={`h-3 w-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full min-w-[240px] pt-1"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div
            id={menuId}
            role="menu"
            aria-label={`${item.label} submenu`}
            className="rounded-xl border border-border bg-background/95 backdrop-blur-xl shadow-lg overflow-hidden"
          >
            <ul className="py-2">
              {item.children.map((c) => (
                <li key={c.to} role="none">
                  <Link
                    to={c.to}
                    role="menuitem"
                    onClick={close}
                    className={`block px-4 py-2.5 text-[13px] font-medium text-ink-soft hover:bg-surface-soft hover:text-primary transition-colors focus-visible:outline-none focus-visible:bg-surface-soft focus-visible:text-primary focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring`}
                    activeProps={{ className: "text-primary bg-primary-soft/40", "aria-current": "page" }}
                  >
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function MobileNavItem({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  const hasChildren = "children" in item && !!item.children;
  const [open, setOpen] = useState(false);
  const subId = useId();
  return (
    <div>
      <div className="flex items-center">
        <Link
          to={item.to}
          onClick={onNavigate}
          className={`flex-1 flex min-h-11 items-center px-3 py-2.5 rounded-md text-sm font-medium text-foreground hover:bg-surface-soft ${focusRing}`}
          activeProps={{ className: "bg-primary-soft text-primary", "aria-current": "page" }}
        >
          {item.label}
        </Link>
        {hasChildren && (
          <button
            type="button"
            aria-label={`${open ? "Collapse" : "Expand"} ${item.label} submenu`}
            aria-expanded={open}
            aria-controls={subId}
            onClick={() => setOpen((v) => !v)}
            className={`ml-1 inline-flex h-11 w-11 items-center justify-center rounded-md text-ink-soft hover:bg-surface-soft ${focusRing}`}
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
        )}
      </div>
      {hasChildren && open && (
        <ul id={subId} className="ml-3 mt-1 mb-1 border-l border-border pl-3 grid gap-1">
          {item.children!.map((c) => (
            <li key={c.to}>
              <Link
                to={c.to}
                onClick={onNavigate}
                className={`flex min-h-11 items-center px-3 py-2 rounded-md text-[13px] text-ink-soft hover:bg-surface-soft hover:text-primary ${focusRing}`}
                activeProps={{ className: "text-primary", "aria-current": "page" }}
              >
                {c.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
